import { registerPlugin } from "@capacitor/core";
import type { PreferencesPlugin } from "@capacitor/preferences";

import { progressConfig } from "../config/progress-config";
import {
  loadBrowserProgress,
  LocalProgressStore,
  type ProgressStorage,
  type ProgressStorageScope,
} from "../persistence/local-progress-store";

export type NativePreferencesPort = Pick<PreferencesPlugin, "get" | "set">;

const nativePreferences = registerPlugin<PreferencesPlugin>("Preferences");
const nativeWriteAttemptLimit = 2;

export interface RuntimeProgress {
  readonly flush: () => Promise<boolean>;
  readonly store: LocalProgressStore;
}

export class NativeProgressStorage implements ProgressStorage {
  private cachedValue: string | null;
  private durableValue: string | null;
  private lastWrite: Promise<boolean> = Promise.resolve(true);
  private latestWriteSequence = 0;
  private writeQueue: Promise<void> = Promise.resolve();

  private constructor(
    private readonly available: boolean,
    initialValue: string | null,
    private readonly preferences: NativePreferencesPort,
    private readonly storageKey: string,
  ) {
    this.cachedValue = initialValue;
    this.durableValue = initialValue;
  }

  public static async load(
    storageKey: string,
    preferences: NativePreferencesPort = nativePreferences,
  ): Promise<NativeProgressStorage> {
    try {
      const result = await preferences.get({ key: storageKey });
      return new NativeProgressStorage(
        true,
        result.value,
        preferences,
        storageKey,
      );
    } catch {
      return new NativeProgressStorage(false, null, preferences, storageKey);
    }
  }

  public getItem(key: string): string | null {
    this.requireAvailableKey(key);
    return this.cachedValue;
  }

  public setItem(key: string, value: string): Promise<boolean> {
    this.requireAvailableKey(key);
    this.latestWriteSequence += 1;
    const sequence = this.latestWriteSequence;
    this.cachedValue = value;
    const write = this.writeQueue
      .then(() => this.writeWithRetry(value))
      .then((durable) => {
        if (durable) {
          this.durableValue = value;
        } else if (sequence === this.latestWriteSequence) {
          this.cachedValue = this.durableValue;
        }
        return durable;
      });
    this.writeQueue = write.then(() => undefined);
    this.lastWrite = write;
    return write;
  }

  public flush(): Promise<boolean> {
    return this.lastWrite;
  }

  private requireAvailableKey(key: string): void {
    if (!this.available) {
      throw new Error("Native preferences are unavailable");
    }
    if (key !== this.storageKey) {
      throw new Error("Native progress storage key is invalid");
    }
  }

  private async writeWithRetry(value: string): Promise<boolean> {
    for (let attempt = 0; attempt < nativeWriteAttemptLimit; attempt += 1) {
      try {
        await this.preferences.set({ key: this.storageKey, value });
        return true;
      } catch {
        continue;
      }
    }
    return false;
  }
}

export const loadRuntimeProgress = async (
  native: boolean,
  scope: ProgressStorageScope,
  preferences: NativePreferencesPort = nativePreferences,
): Promise<RuntimeProgress> => {
  if (!native) {
    return {
      flush: () => Promise.resolve(true),
      store: loadBrowserProgress(scope),
    };
  }

  const storage = await NativeProgressStorage.load(
    progressConfig.storageKey,
    preferences,
  );
  const store = new LocalProgressStore(storage);
  store.load();
  await storage.flush();
  return {
    flush: storage.flush.bind(storage),
    store,
  };
};
